/**
 * Auth-free client for the public timesheet-approval portal (/ta/:token). Reuses the
 * shared public fetch client (no Authorization header — the token in the path is the
 * only credential).
 */
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { TimesheetApprovalDecisionResult, TimesheetApprovalView } from '@catalyst-psa/shared';

import { publicApi } from '../project-portal/publicApi.js';

export function useTimesheetApprovalView(token: string): UseQueryResult<TimesheetApprovalView> {
  return useQuery({
    queryKey: ['timesheet-approval', token] as const,
    queryFn: () => publicApi.get<TimesheetApprovalView>(`/public/timesheet-approvals/${token}`),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useApproveTimesheetByToken(
  token: string,
): UseMutationResult<TimesheetApprovalDecisionResult, unknown, void> {
  return useMutation({
    mutationFn: () =>
      publicApi.post<TimesheetApprovalDecisionResult>(
        `/public/timesheet-approvals/${token}/approve`,
      ),
  });
}

export function useRejectTimesheetByToken(
  token: string,
): UseMutationResult<TimesheetApprovalDecisionResult, unknown, string> {
  return useMutation({
    mutationFn: (reason: string) =>
      publicApi.post<TimesheetApprovalDecisionResult>(
        `/public/timesheet-approvals/${token}/reject`,
        { reason },
      ),
  });
}
